import React from 'react'
import tokml from 'tokml'
import _ from 'lodash'

import {
    H3,
    S2,
    Colors,
    IconButton,
    Toast,
    Card,
    Row,
    Panel,
    ListItem,
    P,
    Popup,
    Button
} from '../../../../components/UI/index.js'
import {
    ColorPicker,
    Input
} from '../../../../components/UI/forms/index.js'
import DeliveryZonesMap from '../../../../components/map/DeliveryZonesMap.js'
import naturalSortBy from 'src/utils/naturalSortBy.js'
import t from 'src/helpers/Languages.js'
import invalidPostalCodeRange from '@utils/invalidPostalCodeRange.js'
import {navOffset} from '../../../../components/navigation/navPosition.js'

class DeliveryZones extends React.Component {
    constructor(props) {
        super(props)

        this.state = {
            postalCodesErrors: {}
        }
    }

    /**
     * Adds new zones with a random route colour, named after their number when they have no name
     * @param {Array<{geoJSON: object, name?: string}>} areas
     * @param {string} defaultNameKey Translation key of the name, given the number of the zone
     */
    addZones(areas, defaultNameKey) {
        const reseller = {...this.props.reseller}
        reseller.settings = {...reseller.settings}
        reseller.settings.deliveryZones = [...(reseller.settings.deliveryZones || [])]

        areas.map(({geoJSON, name}) => {
            reseller.settings.deliveryZones.push({
                id: '_' + Math.random().toString(36).substr(2, 9),
                name: name || t(defaultNameKey, reseller.settings.deliveryZones.length + 1),
                color: Colors.routeColors[_.random(0, Colors.routeColors.length - 1)],
                geoJSON,
                visible: true
            })
        })

        this.props.onChange(reseller, 'deliveryZones')
    }

    onImport(areas, skipped) {
        this.addZones(areas, 'deliveryZoneImportedName')

        if (skipped > 0) {
            this.toast.showToast('warning', t('deliveryZonesImportSkipped', skipped), 4000)
        }
    }

    onChangeArea(id, geoJSON) {
        const reseller = {...this.props.reseller}
        reseller.settings = {...reseller.settings}
        reseller.settings.deliveryZones = reseller.settings.deliveryZones.map((zone) => zone.id === id ? {...zone, geoJSON} : zone)

        this.props.onChange(reseller, 'deliveryZones')
    }

    onChangeValue(path, event) {
        const reseller = {...this.props.reseller}
        reseller.settings = {...reseller.settings}
        reseller.settings.deliveryZones = [...reseller.settings.deliveryZones]

        _.set(reseller.settings, path, event.target.value)

        this.props.onChange(reseller)
    }

    onSaveValue(path, event) {
        const reseller = {...this.props.reseller}
        reseller.settings = {...reseller.settings}
        reseller.settings.deliveryZones = [...reseller.settings.deliveryZones]

        if (event) {
            _.set(reseller.settings, path, event.target.value)
        }

        this.props.onChange(reseller, 'deliveryZones')
    }

    onBlurPostalCodes(zone, index, event) {
        const invalidPart = invalidPostalCodeRange(event.target.value)

        this.setState({postalCodesErrors: {...this.state.postalCodesErrors, [zone.id]: invalidPart ? t('invalidPostalCodeRange', invalidPart) : ''}})

        if (!invalidPart) {
            this.onSaveValue(`deliveryZones.${index}.postalCodes`, event)
        }
    }

    onClickAddPostalCodeZone() {
        const reseller = {...this.props.reseller}
        reseller.settings = {...reseller.settings}
        reseller.settings.deliveryZones = [...reseller.settings.deliveryZones]

        reseller.settings.deliveryZones.push({
            id: '_' + Math.random().toString(36).substr(2, 9),
            name: t('deliveryZoneDefaultName', reseller.settings.deliveryZones.length + 1),
            color: Colors.routeColors[_.random(0, Colors.routeColors.length - 1)],
            type: 'postalCodes',
            postalCodes: '',
            visible: true
        })

        this.props.onChange(reseller, 'deliveryZones')
    }

    onClickRemoveZone(index) {
        const reseller = {...this.props.reseller}
        reseller.settings = {...reseller.settings}
        reseller.settings.deliveryZones = [...reseller.settings.deliveryZones]

        reseller.settings.deliveryZones.splice(index, 1)

        this.props.onChange(reseller, 'deliveryZones')
    }

    onClickShowHide(index) {
        const reseller = {...this.props.reseller}
        reseller.settings = {...reseller.settings}
        reseller.settings.deliveryZones = [...reseller.settings.deliveryZones]

        const zone = reseller.settings.deliveryZones[index]
        zone.visible = !zone.visible

        this.props.onChange(reseller)
    }

    onClickExportLayer(index) {
        const {reseller} = this.props
        const zone = reseller.settings.deliveryZones[index]

        if (!zone.geoJSON) {
            return
        }

        zone.geoJSON.properties = zone.geoJSON.properties || {}

        const kml = tokml(zone.geoJSON, {
            documentName: zone.name
        })

        const element = document.createElement('a')
        const file = new Blob([kml], {
            type: 'application/inkml+xml'
        })
        element.href = URL.createObjectURL(file)
        element.download = `${zone.name}.kml`
        document.body.appendChild(element)
        element.click()
    }

    render() {
        const {reseller} = this.props
        const deliveryZones = reseller.settings?.deliveryZones || []
        const postalCodeZones = deliveryZones.filter((zone) => zone.type === 'postalCodes')
        const drawnZones = deliveryZones.filter((zone) => zone.type !== 'postalCodes')
        const mapStyle = {width: `calc(100vw - ${500 + navOffset().left}px)`, height: `calc(100vh - ${60 + navOffset().top}px)`}

        return (
            <Row style={{gap: 24}}>
                <Card shadow style={{width: 600, padding: 24, height: 'fit-content'}}>

                    <H3 style={{marginBottom: 36}}>{t('deliveryZones')}</H3>

                    <S2 style={{marginBottom: 24}}>
                        {t('deliveryZonesDrawInfoBefore')} <i aria-hidden className='mdi mdi-vector-polygon' style={{fontSize: 20, verticalAlign: 'middle'}} /> {t('deliveryZonesDrawInfoAfter')}
                    </S2>

                    <Button
                        style={{marginLeft: 0, marginBottom: 24}}
                        variant='outline'
                        icon='mdi mdi-plus'
                        label={t('addPostalCodeZone')}
                        onClick={this.onClickAddPostalCodeZone.bind(this)}
                    />

                    {/* Not sorted by name, so a zone doesn't move while its name is typed */}
                    {postalCodeZones.length > 0 &&
                        <Panel style={{marginBottom: 24}}>
                            {postalCodeZones.map((zone) => {
                                const index = reseller.settings.deliveryZones.findIndex((z) => z.id === zone.id)

                                return (
                                    <ListItem key={`zone${zone.id}`} style={{cursor: 'default', gap: 12, paddingTop: 12, paddingBottom: 12}}>
                                        <ColorPicker
                                            value={zone.color}
                                            colors={Colors.routeColors}
                                            onChange={this.onSaveValue.bind(this, `deliveryZones.${index}.color`)}
                                        />

                                        <Input
                                            style={{flex: 1}}
                                            label={t('name')}
                                            value={zone.name || ''}
                                            onChange={this.onChangeValue.bind(this, `deliveryZones.${index}.name`)}
                                            onBlur={this.onSaveValue.bind(this, `deliveryZones.${index}.name`)}
                                        />

                                        <Input
                                            style={{flex: 2}}
                                            label={t('postalCodes')}
                                            info={t('postalCodeZoneFieldInfo')}
                                            value={zone.postalCodes || ''}
                                            isInvalid={this.state.postalCodesErrors[zone.id] || false}
                                            onChange={this.onChangeValue.bind(this, `deliveryZones.${index}.postalCodes`)}
                                            onBlur={this.onBlurPostalCodes.bind(this, zone, index)}
                                        />

                                        <IconButton onClick={this.onClickRemoveZone.bind(this, index)}><i className='mdi mdi-delete' /></IconButton>
                                    </ListItem>
                                )
                            })}
                        </Panel>
                    }

                    {drawnZones.length > 0 &&
                        <Panel style={{maxHeight: `calc(100vh - ${245 + navOffset().top}px)`, overflowY: 'auto', marginBottom: 24}}>
                            {naturalSortBy(drawnZones, 'name').map((zone) => {
                                const index = reseller.settings.deliveryZones.findIndex((z) => z.id === zone.id)

                                return (
                                    <ListItem
                                        key={`zone${zone.id}`}
                                    >
                                        <ColorPicker
                                            value={zone.color}
                                            colors={Colors.routeColors}
                                            onChange={this.onSaveValue.bind(this, `deliveryZones.${index}.color`)}
                                        />

                                        <P style={{flex: 1, marginLeft: 12}}>
                                            {zone.name}
                                        </P>

                                        <IconButton
                                            icon='mdi mdi-pencil'
                                            onClick={async () => {
                                                this.popup.setInputs([{
                                                    type: 'text',
                                                    value: zone.name,
                                                    name: 'name',
                                                    label: t('name')
                                                }])

                                                const values = await this.popup.open(t('changeName'))
                                                if (!values) return
                                                this.popup.close()
                                                this.onSaveValue(`deliveryZones.${index}.name`, {target: {value: values.name}})
                                            }}
                                        />

                                        <IconButton onClick={this.onClickShowHide.bind(this, index)}><i className={zone.visible ? 'mdi mdi-eye-off' : 'mdi mdi-eye'} /></IconButton>

                                        <IconButton onClick={this.onClickExportLayer.bind(this, index)}><i className='mdi mdi-file-export' /></IconButton>

                                        <IconButton onClick={this.onClickRemoveZone.bind(this, index)}><i className='mdi mdi-delete' /></IconButton>

                                    </ListItem>
                                )
                            })}
                        </Panel>
                    }

                    <Toast ref={(ref) => this.toast = ref}></Toast>
                </Card>
                {reseller.settings?.companyAddress ?
                    <DeliveryZonesMap
                        style={mapStyle}
                        center={reseller.settings.companyAddress.position}
                        zones={reseller.settings.deliveryZones}
                        onDraw={(geoJSON) => this.addZones([{geoJSON}], 'deliveryZoneDefaultName')}
                        onImport={this.onImport.bind(this)}
                        onImportError={() => this.toast.showToast('error', t('deliveryZonesImportFailed'), 2500)}
                        onChangeArea={this.onChangeArea.bind(this)}
                    /> :
                    <div style={mapStyle} />
                }

                <Popup ref={(ref) => this.popup = ref} />
            </Row>
        )
    }
}

export default DeliveryZones
